import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Divider,
  Alert,
} from '@mui/material';
import { ArrowBack, Description } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const TermsOfService: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Description sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h3" component="h1">
            Terms of Service
          </Typography>
        </Box>
        
        <Typography variant="body1" color="textSecondary">
          Last updated: October 2024
        </Typography>
      </Box>

      <Card>
        <CardContent sx={{ p: 4 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Important:</strong> This service is for India-based users only. We have not implemented GDPR or HIPAA compliance for EU/US users.
            </Typography>
          </Alert>

          <Typography variant="h5" gutterBottom>
            📜 Terms of Service Agreement
          </Typography>
          
          <Typography variant="body1" paragraph>
            Welcome to 2nd Opinion AI. These Terms of Service ("Terms") govern your use of our medical report analysis service. By using our service, you agree to these terms.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            🏥 Service Description
          </Typography>
          
          <Typography variant="body1" paragraph>
            2nd Opinion AI provides automated analysis of medical reports using artificial intelligence technology. Our service:
          </Typography>
          <ul>
            <li>Analyzes uploaded medical reports (PDF format)</li>
            <li>Generates AI-powered insights and recommendations</li>
            <li>Delivers results via email</li>
            <li>Provides educational information about medical findings</li>
          </ul>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            ⚠️ Medical Disclaimer
          </Typography>
          
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>IMPORTANT MEDICAL DISCLAIMER:</strong> Our service is for informational and educational purposes only. It is NOT a substitute for professional medical advice, diagnosis, or treatment.
            </Typography>
          </Alert>

          <Typography variant="body1" paragraph>
            <strong>You acknowledge that:</strong>
          </Typography>
          <ul>
            <li>AI analysis results are not medical diagnoses</li>
            <li>You should always consult qualified healthcare professionals</li>
            <li>Our service does not replace doctor-patient relationships</li>
            <li>Emergency medical situations require immediate professional care</li>
            <li>We are not responsible for medical decisions based on our analysis</li>
          </ul>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            👤 User Responsibilities
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>You agree to:</strong>
          </Typography>
          <ul>
            <li>Provide accurate and truthful information</li>
            <li>Use the service only for legitimate medical report analysis</li>
            <li>Not upload copyrighted or confidential medical records without permission</li>
            <li>Respect usage limits (5 free analyses per account)</li>
            <li>Not attempt to reverse engineer or misuse our AI technology</li>
            <li>Comply with all applicable laws and regulations</li>
          </ul>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            🔒 Privacy and Data Processing
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>Data Processing Consent:</strong> By using our service, you explicitly consent to:
          </Typography>
          <ul>
            <li>Processing of your medical data by OpenAI (US-based AI service)</li>
            <li>Transfer of your data to OpenAI servers for analysis</li>
            <li>Temporary storage during processing</li>
            <li>AI model training exclusion (your data is not used to train models)</li>
          </ul>

          <Typography variant="body1" paragraph>
            For detailed information about data handling, please review our Privacy Policy.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            🌍 Geographic Limitations
          </Typography>
          
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>India-Only Service:</strong> This service is exclusively for users located in India.
            </Typography>
          </Alert>

          <Typography variant="body1" paragraph>
            <strong>Compliance Notice:</strong>
          </Typography>
          <ul>
            <li>We have NOT implemented GDPR compliance for EU users</li>
            <li>We have NOT implemented HIPAA compliance for US users</li>
            <li>Users outside India use this service at their own risk</li>
            <li>Local privacy laws may not be satisfied by our current implementation</li>
          </ul>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            💳 Usage Limits and Pricing
          </Typography>
          
          <ul>
            <li><strong>Free Tier:</strong> 5 medical report analyses per account</li>
            <li><strong>Usage Tracking:</strong> We monitor usage to enforce limits</li>
            <li><strong>Upgrade Options:</strong> Contact info@2ndopinionai.org for additional analyses</li>
            <li><strong>Fair Use:</strong> Service is intended for personal medical report analysis</li>
          </ul>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            🚫 Prohibited Uses
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>You may NOT use our service for:</strong>
          </Typography>
          <ul>
            <li>Commercial or business purposes without authorization</li>
            <li>Processing reports belonging to other individuals without consent</li>
            <li>Uploading fake, manipulated, or test medical reports</li>
            <li>Attempting to overload or disrupt our systems</li>
            <li>Violating any applicable laws or regulations</li>
            <li>Reverse engineering our AI models or algorithms</li>
          </ul>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            ⚖️ Liability and Disclaimers
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>Service Availability:</strong> We strive for high availability but do not guarantee uninterrupted service.
          </Typography>

          <Typography variant="body1" paragraph>
            <strong>Accuracy Disclaimer:</strong> While we use advanced AI technology, we cannot guarantee 100% accuracy of analysis results.
          </Typography>

          <Typography variant="body1" paragraph>
            <strong>Limitation of Liability:</strong> Our liability is limited to the maximum extent permitted by Indian law.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            📧 Contact Information
          </Typography>
          
          <Typography variant="body1" paragraph>
            For questions about these Terms of Service:
          </Typography>
          
          <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 2 }}>
            <Typography variant="body2">
              <strong>Email:</strong> info@2ndopinionai.org<br />
              <strong>Subject:</strong> Terms of Service Inquiry<br />
              <strong>Website:</strong> https://2ndopinionai.org/
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            🔄 Changes to Terms
          </Typography>
          
          <Typography variant="body1" paragraph>
            We may update these Terms from time to time. Continued use of our service after changes constitutes acceptance of new terms.
          </Typography>

          <Typography variant="body2" color="textSecondary">
            By using our service, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
};

export default TermsOfService;


